import React from 'react'
import { SimpleLayout } from '../../layouts/simple'
import { Guardian, SimpleUser } from '../../providers/context'
import { BlockCalendar } from './components/blockCalendar'
import { ConferenceTime, SharedConferenceData, SimpleUserWithLocation } from './components/conferenceComponents'
import { format, parseISO } from 'date-fns'
import { useRequest } from '../../request'
import { Loader } from '../../components/simple/loader'
import { useTranslation } from '../../providers/translation'
import { useLanguages } from '../../providers/languages'
import { IconAngleDown, IconGlobe } from '../../icons/library'
import { Toasts } from '../../providers/toast'
import { ScheduleRequest, ScheduleRequestModal } from './tabs/scheduleTab'
import { Select } from '../../components/form/select'
import { getComposeLink } from '../../providers/URLParams'
import { Avatar } from '../../components/avatar'
import { Divider } from '../../components/simple/divider'
import { useIsMobile } from '../../hooks/hooks'
import { Button, ButtonStyle } from '../../components/simple/button'
import { Popups } from '../../providers/popups'

type ConferenceInvitation = {
  teacher: SimpleUserWithLocation
  student: SimpleUser
  conference: SharedConferenceData
  existingDay?: string
  allInviteIDs: number[]
  popup?: string
}

export const PTCFamilySchedulerPage: React.FC<{ user: Guardian; appointmentID: number }> = ({
  user,
  appointmentID,
}) => {
  const { data, loading } = useRequest<ConferenceInvitation | string>(
    `/frontend_api/getConferenceInvitation`,
    React.useMemo(() => ({ appointmentID }), [appointmentID])
  )
  React.useEffect(() => {
    if (typeof data == 'string') Toasts.showToast({ message: data, error: true, duration: 10000 })
    else if (data && data.popup) Popups.simplePopup(data.popup)
  }, [data])
  const invitation = typeof data == 'string' ? undefined : data

  return (
    <SimpleLayout
      showName={false}
      headerActions={<div className="ml-4 text-2xl font-semibold">{invitation?.conference.name}</div>}
      helpLink="https://reachmyteach.zendesk.com/hc/en-us/sections/38101452116249-Family"
      verticalScroll={false}
    >
      {invitation ? (
        <PTCFamilyScheduler
          user={user}
          invitation={invitation}
          appointmentID={appointmentID}
          className={loading ? 'pointer-events-none opacity-50' : ''}
        />
      ) : (
        <Loader className="mt-8" />
      )}
    </SimpleLayout>
  )
}

export const PTCFamilyScheduler: React.FC<{
  user: Guardian
  invitation: ConferenceInvitation
  appointmentID: number
  className?: string
}> = ({ user, invitation, appointmentID, className }) => {
  const conference = invitation.conference
  const days = React.useMemo(() => conference.days.map((d) => parseISO(d)), [conference.days])
  const existingDay = invitation.existingDay ? parseISO(invitation.existingDay) : undefined
  const defaultDay = days.length === 1 ? days[0] : existingDay || undefined
  const [selectedDay, setSelectedDay] = React.useState<Date | undefined>(defaultDay)
  React.useEffect(() => {
    if (!days.find((d) => d.getTime() === (selectedDay?.getTime() || 0))) setSelectedDay(defaultDay)
  }, [days])

  const { tr, language, changeLanguage } = useTranslation('ptc')
  const languages = useLanguages()
  const languageOptions = React.useMemo(
    () => languages.getOptions((l) => l.googleTranslatable || l.code == 'en', true),
    [languages]
  )

  const [scheduleRequest, setScheduleRequest] = React.useState<ScheduleRequest | undefined>(undefined)
  React.useEffect(() => {
    setScheduleRequest(undefined)
  }, [appointmentID])

  const {
    data: schedule,
    reload: reloadSchedule,
    loading: loadingSchedule,
  } = useRequest<{
    times: ConferenceTime[]
    existingAppointmentTimes?: ConferenceTime[]
  }>(
    '/frontend_api/getConferenceTimes',
    React.useMemo(
      () => ({
        appointmentID: appointmentID,
        day: selectedDay ? format(selectedDay, 'y-MM-dd') : undefined,
        language: conference.multilingual ? language?.code : undefined,
      }),
      [appointmentID, selectedDay, language]
    ),
    selectedDay !== undefined
  )

  const mobileLayout = useIsMobile()

  const thisInviteIndex = invitation.allInviteIDs.indexOf(appointmentID)
  const otherInvites = [
    ...invitation.allInviteIDs.slice(thisInviteIndex + 1),
    ...invitation.allInviteIDs.slice(0, thisInviteIndex),
  ]

  return (
    <>
      <div
        className={`flex gap-4 sm:mx-auto sm:my-8 sm:max-w-5xl sm:overflow-hidden sm:rounded-lg sm:shadow-special -sm:flex-col -sm:overflow-auto ${className}`}
      >
        {scheduleRequest && (
          <ScheduleRequestModal
            conference={conference}
            scheduleRequest={scheduleRequest}
            onClose={() => setScheduleRequest(undefined)}
            onSchedule={reloadSchedule}
            user={user}
            showConfirmation={true}
            confirmationActions={
              <>
                <Button link="/ptc/allInvites">{tr('All Invites')}</Button>
                {otherInvites.length > 0 && (
                  <Button buttonStyle={ButtonStyle.blue} link={`/ptc/inviteResponse/${otherInvites[0]}`}>
                    {tr('Schedule Next')}
                  </Button>
                )}
              </>
            }
          />
        )}
        <div className="flex w-full flex-shrink basis-0 flex-col items-center bg-logo text-white sm:px-2">
          {!mobileLayout && (
            <>
              <Avatar src={invitation.teacher.avatar} size="xl" className="mb-1 mt-24" />
              <b className="text-lg">{invitation.teacher.name}</b>
              <div className="text-lg">{invitation.student.name}</div>

              <Divider className="mb-4 mt-8 max-w-[70%] opacity-15" />
            </>
          )}
          <BlockCalendar days={days} selectedDay={selectedDay} onSelectDay={setSelectedDay} className="overflow-auto" />

          {schedule && !mobileLayout && (
            <>
              <div className="mb-6" />
              <NoTimesWorkButton invitation={invitation} className="mb-8 ml-6 mt-auto self-start !text-white/80" />
            </>
          )}
        </div>
        <div className="mx-4 flex min-w-0 flex-col gap-2 sm:my-6">
          {languageOptions.length > 0 && language && (
            <Select
              options={languageOptions}
              value={{
                value: language.code,
                name: language.name,
              }}
              onChange={(v) => {
                changeLanguage(v.value)
              }}
              outerClasses="self-start sm:self-end "
              className={`rounded-[16.8px] border border-gray-200 !py-[5.25px] text-[13px]`}
              icon={<IconGlobe />}
              arrow={<IconAngleDown size="sm" className="mr-1 text-gray-300" />}
            />
          )}
          {mobileLayout && (
            <div className="text-xl font-bold">
              {invitation.teacher.name} + {invitation.student.name}
            </div>
          )}
          {schedule && mobileLayout && <NoTimesWorkButton invitation={invitation} className="-mt-2" />}
          {!mobileLayout && selectedDay && <b className="mt-6">{format(selectedDay, 'MMM d, y')}</b>}
          {schedule?.existingAppointmentTimes?.length && (
            <b className="rounded bg-warning px-2 py-1">{tr('To reschedule, first remove your appointment.')}</b>
          )}
          {selectedDay ? (
            <div
              className={`-mx-1 my-4 flex flex-col gap-2 p-1 sm:overflow-auto ${loadingSchedule ? 'pointer-events-none opacity-50' : ''}`}
            >
              {schedule?.times.map((time) => (
                <ConferenceTime
                  key={time.date}
                  conference={conference}
                  time={time}
                  user={user}
                  reloadSchedule={reloadSchedule}
                  scheduleAppointment={() =>
                    setScheduleRequest({
                      time,
                      language:
                        invitation.conference.multilingual && language && language.code !== 'en'
                          ? { name: language.name, value: language.code }
                          : undefined,
                      teacher: invitation.teacher,
                      location: invitation.teacher.location,
                      student: invitation.student,
                    })
                  }
                  selectedLanguage={
                    invitation.conference.multilingual && language
                      ? {
                          value: language?.code,
                          name: language?.name,
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <div className="mt-4 flex justify-center rounded bg-slate-50 p-2 font-bold">
              {tr('Select a day to see available times')}
            </div>
          )}
          {!mobileLayout && (
            <div className="mt-auto flex justify-end gap-2">
              <Button link={'/ptc/allInvites'}>{tr('Back')}</Button>
              {otherInvites.length > 0 && (
                <Button link={`/ptc/inviteResponse/${otherInvites[0]}`}>{tr('Next Teacher')}</Button>
              )}
            </div>
          )}
        </div>
      </div>
      {mobileLayout && (
        <div className="mt-auto flex w-full justify-between gap-2 px-4 py-3 shadow-header">
          <Button link={'/ptc/allInvites'}>{tr('Back')}</Button>
          {otherInvites.length > 0 && (
            <Button link={`/ptc/inviteResponse/${otherInvites[0]}`}>{tr('Next Teacher')}</Button>
          )}
        </div>
      )}
    </>
  )
}

const NoTimesWorkButton: React.FC<{ invitation: ConferenceInvitation; className?: string }> = ({
  invitation,
  className,
}) => {
  const { tr } = useTranslation('ptc')
  return (
    <a
      className={`cursor:pointer text-nowrap text-neutral-600 underline ${className}`}
      target="_blank"
      href={getComposeLink({ teachers: invitation.teacher.id, regardingStudent: invitation.student.id })}
    >
      {tr('None of these times work for me')}
    </a>
  )
}
